/**
 * AudioManager - Procedural sound generation for Space Invaders 2030
 *
 * Uses the Web Audio API to generate authentic retro sounds similar to
 * the original Space Invaders arcade game. All sounds are synthesized
 * procedurally without external audio files.
 */

import { PowerUpType } from '../powerups/types'

export class AudioManager {
  private audioContext: AudioContext | null = null
  private masterGain: GainNode | null = null
  private isMuted = false
  private isInitialized = false

  // March sound state - classic 4-note descending pattern
  private marchNoteIndex = 0
  private readonly marchFrequencies = [55, 49, 43.65, 38.89] // A1, G1, F1, D#1

  /**
   * Helper method for exponential decay - avoids polluting AudioParam prototype.
   * Uses exponentialRampToValueAtTime but clamps minimum value to avoid
   * the "zero or negative value" error that exponentialRamp throws.
   */
  private exponentialDecayTo(param: AudioParam, value: number, endTime: number): void {
    param.exponentialRampToValueAtTime(Math.max(0.0001, value), endTime)
  }

  /**
   * Initialize the audio context. Must be called after user interaction
   * due to browser autoplay policies.
   */
  async init(): Promise<void> {
    if (this.isInitialized) return

    try {
      this.audioContext = new (window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
      this.masterGain = this.audioContext.createGain()
      this.masterGain.connect(this.audioContext.destination)
      this.masterGain.gain.value = 0.3 // Default volume
      this.isInitialized = true
    } catch (error) {
      console.warn('Web Audio API not supported:', error)
    }
  }

  /**
   * Resume audio context if suspended (required by some browsers)
   */
  async resume(): Promise<void> {
    if (this.audioContext?.state === 'suspended') {
      await this.audioContext.resume()
    }
  }

  /**
   * Set master volume
   */
  setVolume(value: number): void {
    if (this.masterGain) {
      this.masterGain.gain.value = Math.max(0, Math.min(1, value))
    }
  }

  /**
   * Toggle mute
   */
  toggleMute(): boolean {
    this.isMuted = !this.isMuted
    if (this.masterGain) {
      this.masterGain.gain.value = this.isMuted ? 0 : 0.3
    }
    return this.isMuted
  }

  /**
   * Get current mute state
   */
  getMuted(): boolean {
    return this.isMuted
  }

  /**
   * Play the classic march/thump sound that accompanies invader animation.
   * Cycles through 4 descending bass notes.
   */
  playMarchSound(): void {
    if (!this.audioContext || !this.masterGain || this.isMuted) return

    const oscillator = this.audioContext.createOscillator()
    const gainNode = this.audioContext.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(this.masterGain)

    // Use square wave for that classic 8-bit sound
    oscillator.type = 'square'
    oscillator.frequency.value = this.marchFrequencies[this.marchNoteIndex]

    // Cycle through the 4-note pattern
    this.marchNoteIndex = (this.marchNoteIndex + 1) % 4

    const now = this.audioContext.currentTime

    // Short, punchy envelope
    gainNode.gain.setValueAtTime(0.5, now)
    this.exponentialDecayTo(gainNode.gain, 0.01, now + 0.1)

    oscillator.start(now)
    oscillator.stop(now + 0.1)
  }

  /**
   * Reset the march note sequence (e.g., when restarting the game)
   */
  resetMarchSequence(): void {
    this.marchNoteIndex = 0
  }

  /**
   * Play player shooting sound - a short high-pitched zap
   */
  playShootSound(): void {
    if (!this.audioContext || !this.masterGain || this.isMuted) return

    const oscillator = this.audioContext.createOscillator()
    const gainNode = this.audioContext.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(this.masterGain)

    oscillator.type = 'square'

    const now = this.audioContext.currentTime

    // Descending frequency sweep for laser effect
    oscillator.frequency.setValueAtTime(1200, now)
    oscillator.frequency.exponentialRampToValueAtTime(200, now + 0.1)

    gainNode.gain.setValueAtTime(0.3, now)
    this.exponentialDecayTo(gainNode.gain, 0.01, now + 0.1)

    oscillator.start(now)
    oscillator.stop(now + 0.1)
  }

  /**
   * Play invader explosion sound - white noise burst
   */
  playExplosionSound(): void {
    if (!this.audioContext || !this.masterGain || this.isMuted) return

    // Create white noise using a buffer
    const bufferSize = this.audioContext.sampleRate * 0.15 // 150ms
    const buffer = this.audioContext.createBuffer(
      1,
      bufferSize,
      this.audioContext.sampleRate
    )
    const data = buffer.getChannelData(0)

    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1
    }

    const noiseSource = this.audioContext.createBufferSource()
    noiseSource.buffer = buffer

    // Filter the noise for a more satisfying explosion
    const filter = this.audioContext.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 1500

    const gainNode = this.audioContext.createGain()

    noiseSource.connect(filter)
    filter.connect(gainNode)
    gainNode.connect(this.masterGain)

    const now = this.audioContext.currentTime

    // Fade out envelope
    gainNode.gain.setValueAtTime(0.4, now)
    this.exponentialDecayTo(gainNode.gain, 0.01, now + 0.15)

    // Sweep filter down for more impact
    filter.frequency.setValueAtTime(1500, now)
    filter.frequency.exponentialRampToValueAtTime(200, now + 0.15)

    noiseSource.start(now)
    noiseSource.stop(now + 0.15)
  }

  /**
   * Play player death sound - longer descending tone with noise
   */
  playPlayerDeathSound(): void {
    if (!this.audioContext || !this.masterGain || this.isMuted) return

    // Descending tone component
    const oscillator = this.audioContext.createOscillator()
    const oscGain = this.audioContext.createGain()

    oscillator.connect(oscGain)
    oscGain.connect(this.masterGain)

    oscillator.type = 'sawtooth'

    const now = this.audioContext.currentTime

    // Long descending sweep
    oscillator.frequency.setValueAtTime(800, now)
    oscillator.frequency.exponentialRampToValueAtTime(50, now + 0.5)

    oscGain.gain.setValueAtTime(0.3, now)
    this.exponentialDecayTo(oscGain.gain, 0.01, now + 0.5)

    oscillator.start(now)
    oscillator.stop(now + 0.5)

    // Add noise burst
    const bufferSize = this.audioContext.sampleRate * 0.3
    const buffer = this.audioContext.createBuffer(
      1,
      bufferSize,
      this.audioContext.sampleRate
    )
    const data = buffer.getChannelData(0)

    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1
    }

    const noiseSource = this.audioContext.createBufferSource()
    noiseSource.buffer = buffer

    const filter = this.audioContext.createBiquadFilter()
    filter.type = 'bandpass'
    filter.frequency.value = 400
    filter.Q.value = 2

    const noiseGain = this.audioContext.createGain()

    noiseSource.connect(filter)
    filter.connect(noiseGain)
    noiseGain.connect(this.masterGain)

    noiseGain.gain.setValueAtTime(0.2, now)
    this.exponentialDecayTo(noiseGain.gain, 0.01, now + 0.3)

    noiseSource.start(now)
    noiseSource.stop(now + 0.3)
  }

  /**
   * Play game over sound - ominous descending tones
   */
  playGameOverSound(): void {
    if (!this.audioContext || !this.masterGain || this.isMuted) return

    const now = this.audioContext.currentTime

    // Play 3 descending notes
    const notes = [220, 164.81, 110] // A3, E3, A2
    notes.forEach((freq, index) => {
      const oscillator = this.audioContext!.createOscillator()
      const gainNode = this.audioContext!.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(this.masterGain!)

      oscillator.type = 'square'
      oscillator.frequency.value = freq

      const startTime = now + index * 0.3
      gainNode.gain.setValueAtTime(0.3, startTime)
      this.exponentialDecayTo(gainNode.gain, 0.01, startTime + 0.3)

      oscillator.start(startTime)
      oscillator.stop(startTime + 0.3)
    })
  }

  /**
   * Play victory sound - ascending triumphant tones
   */
  playVictorySound(): void {
    if (!this.audioContext || !this.masterGain || this.isMuted) return

    const now = this.audioContext.currentTime

    // Play ascending arpeggio
    const notes = [261.63, 329.63, 392, 523.25] // C4, E4, G4, C5
    notes.forEach((freq, index) => {
      const oscillator = this.audioContext!.createOscillator()
      const gainNode = this.audioContext!.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(this.masterGain!)

      oscillator.type = 'square'
      oscillator.frequency.value = freq

      const startTime = now + index * 0.15
      gainNode.gain.setValueAtTime(0.25, startTime)
      this.exponentialDecayTo(gainNode.gain, 0.01, startTime + 0.2)

      oscillator.start(startTime)
      oscillator.stop(startTime + 0.2)
    })
  }

  /**
   * Play power-up pickup sound - ascending sparkle effect
   */
  playPowerUpPickupSound(): void {
    if (!this.audioContext || !this.masterGain || this.isMuted) return

    const now = this.audioContext.currentTime

    // Quick ascending arpeggio
    const notes = [523.25, 659.25, 783.99, 1046.5] // C5, E5, G5, C6
    notes.forEach((freq, index) => {
      const oscillator = this.audioContext!.createOscillator()
      const gainNode = this.audioContext!.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(this.masterGain!)

      oscillator.type = 'sine'
      oscillator.frequency.value = freq

      const startTime = now + index * 0.05
      gainNode.gain.setValueAtTime(0.2, startTime)
      this.exponentialDecayTo(gainNode.gain, 0.01, startTime + 0.1)

      oscillator.start(startTime)
      oscillator.stop(startTime + 0.1)
    })
  }

  /**
   * Play power-up activation sound - unique per type
   */
  playPowerUpActivateSound(type: PowerUpType): void {
    if (!this.audioContext || !this.masterGain || this.isMuted) return

    const now = this.audioContext.currentTime

    switch (type) {
      case PowerUpType.RAPID_FIRE:
        this.playRapidFireActivate(now)
        break
      case PowerUpType.SHIELD:
        this.playShieldActivate(now)
        break
      case PowerUpType.MULTI_SHOT:
        this.playMultiShotActivate(now)
        break
      case PowerUpType.SLOW_MOTION:
        this.playSlowMotionActivate(now)
        break
      case PowerUpType.SCORE_MULTIPLIER:
        this.playScoreMultiplierActivate(now)
        break
    }
  }

  private playRapidFireActivate(now: number): void {
    // Fast pulsing sound
    for (let i = 0; i < 4; i++) {
      const oscillator = this.audioContext!.createOscillator()
      const gainNode = this.audioContext!.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(this.masterGain!)

      oscillator.type = 'square'
      oscillator.frequency.value = 880 + i * 220

      const startTime = now + i * 0.06
      gainNode.gain.setValueAtTime(0.15, startTime)
      this.exponentialDecayTo(gainNode.gain, 0.01, startTime + 0.05)

      oscillator.start(startTime)
      oscillator.stop(startTime + 0.05)
    }
  }

  private playShieldActivate(now: number): void {
    // Warm, enveloping sound
    const oscillator = this.audioContext!.createOscillator()
    const gainNode = this.audioContext!.createGain()
    const filter = this.audioContext!.createBiquadFilter()

    oscillator.connect(filter)
    filter.connect(gainNode)
    gainNode.connect(this.masterGain!)

    oscillator.type = 'sawtooth'
    oscillator.frequency.setValueAtTime(200, now)
    oscillator.frequency.exponentialRampToValueAtTime(400, now + 0.3)

    filter.type = 'lowpass'
    filter.frequency.setValueAtTime(800, now)
    filter.frequency.exponentialRampToValueAtTime(2000, now + 0.3)

    gainNode.gain.setValueAtTime(0.2, now)
    this.exponentialDecayTo(gainNode.gain, 0.01, now + 0.4)

    oscillator.start(now)
    oscillator.stop(now + 0.4)
  }

  private playMultiShotActivate(now: number): void {
    // Triple tone burst
    const frequencies = [330, 415, 523] // E4, G#4, C5 (diminished chord)
    frequencies.forEach((freq, index) => {
      const oscillator = this.audioContext!.createOscillator()
      const gainNode = this.audioContext!.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(this.masterGain!)

      oscillator.type = 'triangle'
      oscillator.frequency.value = freq

      const startTime = now + index * 0.02
      gainNode.gain.setValueAtTime(0.15, startTime)
      this.exponentialDecayTo(gainNode.gain, 0.01, startTime + 0.2)

      oscillator.start(startTime)
      oscillator.stop(startTime + 0.2)
    })
  }

  private playSlowMotionActivate(now: number): void {
    // Deep, slowing-down sound
    const oscillator = this.audioContext!.createOscillator()
    const gainNode = this.audioContext!.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(this.masterGain!)

    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(440, now)
    oscillator.frequency.exponentialRampToValueAtTime(110, now + 0.5)

    gainNode.gain.setValueAtTime(0.25, now)
    this.exponentialDecayTo(gainNode.gain, 0.01, now + 0.5)

    oscillator.start(now)
    oscillator.stop(now + 0.5)
  }

  private playScoreMultiplierActivate(now: number): void {
    // Coin-like chime
    const oscillator = this.audioContext!.createOscillator()
    const oscillator2 = this.audioContext!.createOscillator()
    const gainNode = this.audioContext!.createGain()

    oscillator.connect(gainNode)
    oscillator2.connect(gainNode)
    gainNode.connect(this.masterGain!)

    oscillator.type = 'sine'
    oscillator.frequency.value = 1318.5 // E6

    oscillator2.type = 'sine'
    oscillator2.frequency.value = 1568 // G6

    gainNode.gain.setValueAtTime(0.15, now)
    this.exponentialDecayTo(gainNode.gain, 0.01, now + 0.3)

    oscillator.start(now)
    oscillator.stop(now + 0.3)
    oscillator2.start(now)
    oscillator2.stop(now + 0.3)
  }

  /**
   * Play power-up deactivation sound - descending fade
   */
  playPowerUpDeactivateSound(): void {
    if (!this.audioContext || !this.masterGain || this.isMuted) return

    const now = this.audioContext.currentTime

    const oscillator = this.audioContext.createOscillator()
    const gainNode = this.audioContext.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(this.masterGain)

    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(523.25, now) // C5
    oscillator.frequency.exponentialRampToValueAtTime(261.63, now + 0.2) // C4

    gainNode.gain.setValueAtTime(0.15, now)
    this.exponentialDecayTo(gainNode.gain, 0.01, now + 0.2)

    oscillator.start(now)
    oscillator.stop(now + 0.2)
  }

  /**
   * Play shield hit/absorb sound
   */
  playShieldHitSound(): void {
    if (!this.audioContext || !this.masterGain || this.isMuted) return

    const now = this.audioContext.currentTime

    // Impact with metallic ring
    const oscillator = this.audioContext.createOscillator()
    const gainNode = this.audioContext.createGain()
    const filter = this.audioContext.createBiquadFilter()

    oscillator.connect(filter)
    filter.connect(gainNode)
    gainNode.connect(this.masterGain)

    oscillator.type = 'triangle'
    oscillator.frequency.value = 800

    filter.type = 'bandpass'
    filter.frequency.value = 1200
    filter.Q.value = 5

    gainNode.gain.setValueAtTime(0.3, now)
    this.exponentialDecayTo(gainNode.gain, 0.01, now + 0.15)

    oscillator.start(now)
    oscillator.stop(now + 0.15)

    // Add noise burst
    const bufferSize = this.audioContext.sampleRate * 0.05
    const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate)
    const data = buffer.getChannelData(0)

    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1
    }

    const noiseSource = this.audioContext.createBufferSource()
    noiseSource.buffer = buffer

    const noiseFilter = this.audioContext.createBiquadFilter()
    noiseFilter.type = 'highpass'
    noiseFilter.frequency.value = 2000

    const noiseGain = this.audioContext.createGain()
    noiseGain.gain.setValueAtTime(0.1, now)
    this.exponentialDecayTo(noiseGain.gain, 0.01, now + 0.05)

    noiseSource.connect(noiseFilter)
    noiseFilter.connect(noiseGain)
    noiseGain.connect(this.masterGain)

    noiseSource.start(now)
    noiseSource.stop(now + 0.05)
  }

  /**
   * Clean up audio context
   */
  destroy(): void {
    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
      this.masterGain = null
      this.isInitialized = false
    }
  }
}

// Singleton instance for easy access across scenes
export const audioManager = new AudioManager()
